import type { EmployeeConfig, RequestStatus, RequestType, WebRequestTypeConfig } from "./employee-api";

/** Request types as staff see them, in the order the request form offers them. */
export const REQUEST_TYPE_LABELS: Array<{ value: RequestType; label: string }> = [
  { value: "LEAVE", label: "Leave" },
  { value: "ATTENDANCE_CORRECTION", label: "Attendance correction" },
  { value: "MISSED_CHECK_IN", label: "Missed check-in" },
  { value: "MISSED_CHECK_OUT", label: "Missed check-out" },
  { value: "INCORRECT_LOCATION", label: "Incorrect location" },
  { value: "OVERTIME", label: "Overtime" },
  { value: "SHIFT_SWAP", label: "Shift swap" },
  { value: "WORK_FROM_ANYWHERE", label: "Work from anywhere" },
  { value: "OTHER", label: "Other" },
];

export const requestTypeLabel = (t: RequestType) =>
  REQUEST_TYPE_LABELS.find((o) => o.value === t)?.label ?? t;

/**
 * A request's type as a person reads it (FEAT-2026-031, BR-213): the tenant's
 * name when renamed, else the built-in one. Admins also see the built-in name
 * beside a renamed one, so HR always knows what it is.
 */
export function displayTypeName(
  type: RequestType,
  typeLabel: string | null | undefined,
  opts: { admin?: boolean } = {},
): string {
  const builtIn = requestTypeLabel(type);
  if (!typeLabel) return builtIn;
  return opts.admin ? `${typeLabel} (${builtIn})` : typeLabel;
}

/** One type the web request form offers. */
export interface WebRequestType {
  value: RequestType;
  label: string;
  description: string | null;
  dateMode: "single" | "range";
  attachment: { required: boolean; instruction: string | null };
}

const KNOWN_TYPES = new Set<string>(REQUEST_TYPE_LABELS.map((o) => o.value));
/** Types the built-in list gives a range (BR-031). */
const RANGED_BY_DEFAULT = new Set<RequestType>(["LEAVE", "WORK_FROM_ANYWHERE"]);

/**
 * The types the web request form offers (FEAT-2026-031). The tenant's list
 * from config when it has at least one usable entry; otherwise the built-in
 * list, as before, without Shift swap (retired, R4a) and with Work from
 * anywhere only while the tenant has WFA on (BR-161). A key the portal
 * doesn't know is left out: the portal deploys with the API, so it learns
 * new types then.
 */
export function webRequestTypes(config: EmployeeConfig | null): WebRequestType[] {
  const listed = config?.requests?.types;
  const usable = (Array.isArray(listed) ? listed : []).filter(
    (t): t is WebRequestTypeConfig =>
      !!t && typeof t === "object" && typeof t.key === "string" && KNOWN_TYPES.has(t.key) && t.key !== "SHIFT_SWAP" &&
      (t.dateMode === "single" || t.dateMode === "range"),
  );
  if (usable.length) {
    return usable.map((t) => ({
      value: t.key as RequestType,
      label: t.label || requestTypeLabel(t.key as RequestType),
      description: t.description ?? null,
      dateMode: t.dateMode,
      attachment: {
        required: t.attachment?.required === true,
        instruction: t.attachment?.instruction ?? null,
      },
    }));
  }
  const rules = config?.requests?.attachmentRules ?? [];
  return REQUEST_TYPE_LABELS.filter(
    (o) => o.value !== "SHIFT_SWAP" && (o.value !== "WORK_FROM_ANYWHERE" || config?.attendance.wfaEnabled),
  ).map((o) => {
    const rule = rules.find((r) => r.type === o.value);
    return {
      value: o.value,
      label: o.label,
      description: null,
      dateMode: RANGED_BY_DEFAULT.has(o.value) ? "range" : "single",
      attachment: { required: !!rule, instruction: rule?.instruction ?? null },
    };
  });
}

export function requestStatusVariant(s: RequestStatus): "default" | "secondary" | "outline" | "destructive" {
  switch (s) {
    case "APPROVED":
      return "default";
    case "REJECTED":
      return "destructive";
    case "PENDING":
    case "NEEDS_CLARIFICATION":
      return "secondary";
    default:
      return "outline";
  }
}

export const requestStatusLabel = (s: RequestStatus) =>
  s === "NEEDS_CLARIFICATION" ? "Needs info" : s.charAt(0) + s.slice(1).toLowerCase();
